import { Chip, ChipInput, ChipList, ChipRemove, TextTooltip } from '@components'
import { InputWrapper } from '@components/Inputs/Input'
import { getSettingsPageUrl } from '@constants'
import { useBusinessContext } from '@root/context'
import { fetchPeople, IUsershipPerson } from '@root/query'
import { getBusinessRoleIcon, roleById } from '@root/query/usershipTags'
import { getUserDisplayName } from '@root/utils'
import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { FaExclamationTriangle, FaTimes } from 'react-icons/fa'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useWorkflowCatalogs } from './WorkflowCatalogsContext'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const isValidEmail = (value: string) => EMAIL_PATTERN.test(value)

const foldDiacritics = (value: string) =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()

const membersWithTag = (people: IUsershipPerson[], tag: string) =>
  people.filter(person => person.tags?.includes(tag))

const formatNameList = (names: string[], language: string, overflow: (count: number) => string) => {
  if (names.length > 3) {
    return `${names[0]}, ${names[1]} ${overflow(names.length - 2)}`
  }
  return new Intl.ListFormat(language, { type: 'conjunction' }).format(names)
}

interface EmailRecipientsFieldProps {
  inputId: string
  recipients: string[]
  recipientTags: string[]
  error?: string
  onChangeRecipients: (recipients: string[]) => void
  onChangeTags: (tags: string[]) => void
}

export const EmailRecipientsField: React.FC<EmailRecipientsFieldProps> = ({
  inputId,
  recipients,
  recipientTags,
  error,
  onChangeRecipients,
  onChangeTags
}) => {
  const [t, i18n] = useTranslation()
  const { slug } = useBusinessContext()
  const { usershipTags: roles } = useWorkflowCatalogs()
  const [draft, setDraft] = useState('')
  const [isFocused, setFocused] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // Mirrors `draft` so handlers that run before React re-renders see the cleared value.
  const pendingDraft = useRef('')

  const { data } = useQuery({
    queryKey: [slug, 'people'],
    queryFn: () => fetchPeople({ slug }, { page_size: 5000 })
  })
  const people = data?.results ?? []

  const search = foldDiacritics(draft.trim())
  const roleSuggestions = isOpen
    ? roles
        .filter(role => !recipientTags.includes(role.id))
        .filter(role => {
          if (!search) return true
          return foldDiacritics(role.plural).includes(search)
        })
    : []
  const suggestions = isOpen
    ? people
        .filter(person => person.email && !recipients.includes(person.email.toLowerCase()))
        .filter(
          person =>
            !search ||
            foldDiacritics(getUserDisplayName(person) ?? '').includes(search) ||
            foldDiacritics(person.email).includes(search)
        )
    : []

  const updateDraft = (next: string) => {
    pendingDraft.current = next
    setDraft(next)
  }

  const addRecipient = (raw: string) => {
    const address = raw.trim().toLowerCase()
    updateDraft('')
    if (!address || recipients.includes(address)) return
    onChangeRecipients([...recipients, address])
  }

  const addRole = (tag: string) => {
    updateDraft('')
    if (recipientTags.includes(tag)) return
    onChangeTags([...recipientTags, tag])
  }

  // Leaving the field / Enter / comma: only commit a valid address.
  const dismiss = () => {
    if (isValidEmail(pendingDraft.current.trim())) {
      addRecipient(pendingDraft.current)
    } else {
      updateDraft('')
    }
    setOpen(false)
  }
  const dismissRef = useRef(dismiss)
  dismissRef.current = dismiss

  const removeRecipient = (address: string) => {
    onChangeRecipients(recipients.filter(recipient => recipient !== address))
  }

  const removeRole = (tag: string) => {
    onChangeTags(recipientTags.filter(current => current !== tag))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      dismiss()
    } else if (event.key === 'Backspace' && !draft) {
      if (recipients.length > 0) {
        removeRecipient(recipients[recipients.length - 1])
      } else if (recipientTags.length > 0) {
        removeRole(recipientTags[recipientTags.length - 1])
      }
    } else if (event.key === 'Escape') {
      updateDraft('')
      setOpen(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const handlePointerDown = (event: PointerEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return
      dismissRef.current()
    }
    // Capture phase: the inspector panel stops pointerdown propagation.
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [isOpen])

  const showSuggestions = roleSuggestions.length > 0 || suggestions.length > 0

  return (
    <div ref={rootRef}>
      <InputWrapper
        inputId={inputId}
        label={t('workflows.config.emailSend.recipients')}
        required
        isFocused={isFocused}
        isInvalid={Boolean(error)}
        errorMsg={error}
      >
        <ChipList>
          {recipientTags.map(tag => {
            const members = membersWithTag(people, tag)
            const empty = members.length === 0
            const role = roleById(roles, tag)
            const RoleIcon = getBusinessRoleIcon(tag)
            return (
              <RoleChip key={tag} $empty={empty}>
                <RoleIcon />
                <span>{role.plural}</span>
                <RoleCount $empty={empty}>{members.length}</RoleCount>
                <ChipRemove type="button" aria-label={tag} onClick={() => removeRole(tag)}>
                  <FaTimes />
                </ChipRemove>
              </RoleChip>
            )
          })}
          {recipients.map(address => (
            <Chip key={address}>
              <span>{address}</span>
              <ChipRemove
                type="button"
                aria-label={address}
                onClick={() => removeRecipient(address)}
              >
                <FaTimes />
              </ChipRemove>
            </Chip>
          ))}
          <ChipInput
            id={inputId}
            ref={inputRef}
            // Chrome ignores "off" for fields it classifies as email; an unknown token disables it.
            autoComplete="workflow-recipient"
            data-1p-ignore
            data-lpignore="true"
            value={draft}
            placeholder={
              recipients.length === 0 && recipientTags.length === 0
                ? t('workflows.config.emailSend.recipientsPlaceholderEmpty')
                : undefined
            }
            onChange={event => {
              updateDraft(event.target.value)
              setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              setFocused(true)
              setOpen(true)
            }}
            onBlur={event => {
              setFocused(false)
              // A null relatedTarget means a mouse press somewhere non-focusable
              // (or a button in Safari); the document listener handles that case.
              const next = event.relatedTarget as Node | null
              if (next && !rootRef.current?.contains(next)) {
                dismiss()
              }
            }}
          />
        </ChipList>
        {showSuggestions && (
          <Suggestions>
            {roleSuggestions.length > 0 && (
              <>
                <SuggestionGroup>{t('workflows.config.emailSend.groupRoles')}</SuggestionGroup>
                {roleSuggestions.map(role => {
                  const members = membersWithTag(people, role.id)
                  const RoleIcon = getBusinessRoleIcon(role.id)
                  return (
                    <Suggestion
                      key={role.id}
                      type="button"
                      onClick={() => {
                        addRole(role.id)
                        setOpen(false)
                        inputRef.current?.focus()
                      }}
                    >
                      <RoleMark>
                        <RoleIcon />
                      </RoleMark>
                      <SuggestionCopy>
                        <SuggestionName>
                          {t('workflows.config.emailSend.roleOption', {
                            plural: role.plural.toLowerCase()
                          })}
                        </SuggestionName>
                        <SuggestionEmail>
                          {t('workflows.config.emailSend.roleOptionMeta', {
                            count: members.length
                          })}
                        </SuggestionEmail>
                      </SuggestionCopy>
                    </Suggestion>
                  )
                })}
              </>
            )}
            {suggestions.length > 0 && (
              <>
                {roleSuggestions.length > 0 && <SuggestionDivider />}
                <SuggestionGroup>{t('workflows.config.emailSend.groupPeople')}</SuggestionGroup>
                {suggestions.map(person => (
                  <Suggestion
                    key={person.id}
                    type="button"
                    onClick={() => {
                      addRecipient(person.email)
                      setOpen(false)
                      inputRef.current?.focus()
                    }}
                  >
                    <SuggestionCopy>
                      <SuggestionName>
                        {getUserDisplayName(person)}
                        {person.tags?.map(tag => {
                          const role = roles.find(item => item.id === tag)
                          if (!role) return null
                          const RoleIcon = getBusinessRoleIcon(tag)
                          return (
                            <TextTooltip key={tag} tooltip={role.name}>
                              <PersonRoleIcon>
                                <RoleIcon />
                              </PersonRoleIcon>
                            </TextTooltip>
                          )
                        })}
                      </SuggestionName>
                      <SuggestionEmail>{person.email}</SuggestionEmail>
                    </SuggestionCopy>
                  </Suggestion>
                ))}
              </>
            )}
          </Suggestions>
        )}
      </InputWrapper>
      {recipients.length === 0 && recipientTags.length === 0 && (
        <HelperText>{t('workflows.config.emailSend.recipientsHint')}</HelperText>
      )}
      {recipientTags.map(tag => {
        const members = membersWithTag(people, tag)
        const role = roleById(roles, tag)
        if (members.length === 0) {
          return (
            <RoleNote key={tag} $empty>
              <FaExclamationTriangle />
              <span>
                <Trans
                  i18nKey="workflows.config.emailSend.roleEmpty"
                  values={{ role: role.name }}
                  components={{ b: <b /> }}
                />{' '}
                <SettingsLink to={getSettingsPageUrl(slug, { page: 'people' })}>
                  {t('workflows.config.emailSend.roleEmptyLink')}
                </SettingsLink>
              </span>
            </RoleNote>
          )
        }
        const names = members.map(person => getUserDisplayName(person) || person.email)
        const RoleIcon = getBusinessRoleIcon(tag)
        return (
          <RoleNote key={tag}>
            <RoleIcon />
            <span>
              <Trans
                i18nKey="workflows.config.emailSend.roleResolves"
                values={{
                  role: role.plural,
                  names: formatNameList(names, i18n.language, count =>
                    t('workflows.config.emailSend.roleResolvesOverflow', { count })
                  )
                }}
                components={{ b: <b /> }}
              />
            </span>
          </RoleNote>
        )
      })}
    </div>
  )
}

const HelperText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`

const RoleChip = styled.span<{ $empty: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  padding: 1px 2px 1px ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme, $empty }) =>
    $empty ? theme.colors.neutralWhite : theme.dashboard.infoTint};
  border: 1px solid ${({ theme, $empty }) => ($empty ? theme.dashboard.caution : 'transparent')};
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme, $empty }) => ($empty ? theme.dashboard.caution : theme.dashboard.infoDeep)};

  svg {
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
  }
`

const RoleCount = styled.span<{ $empty: boolean }>`
  padding: 0 5px;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background: ${({ theme, $empty }) =>
    $empty ? theme.dashboard.cautionTint : theme.colors.neutralWhite};
  font-size: ${({ theme }) => theme.fontSize.px10}rem;
  line-height: 16px;
`

const Suggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 290px;
  max-height: 200px;
  overflow: auto;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.sm}rem;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.2);
`

const SuggestionGroup = styled.div`
  padding: 0.18rem ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.xxs}rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.iconGray};
`

const SuggestionDivider = styled.div`
  height: 1px;
  margin: ${({ theme }) => theme.spacing.xs}rem 0;
  background: ${({ theme }) => theme.colors.neutralGray};
`

const Suggestion = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
  }
`

const SuggestionName = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.metalGray};
`

const SuggestionEmail = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const SuggestionCopy = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`

const RoleMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ theme }) => theme.dashboard.infoTint};
  color: ${({ theme }) => theme.dashboard.infoDeep};

  svg {
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
  }
`

const PersonRoleIcon = styled.span`
  display: inline-flex;
  margin-left: ${({ theme }) => theme.spacing.xxs}rem;
  color: ${({ theme }) => theme.colors.iconGray};

  svg {
    width: 9px;
    height: 9px;
  }
`

const RoleNote = styled.div<{ $empty?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  margin-top: ${({ theme }) => theme.spacing.xs}rem;
  padding: ${({ theme }) => theme.spacing.xs}rem ${({ theme }) => theme.spacing.sm}rem;
  border: 1px solid ${({ theme, $empty }) => ($empty ? 'transparent' : theme.colors.neutralGray)};
  border-radius: ${({ theme }) => theme.borderRadius.sm}rem;
  background: ${({ theme, $empty }) =>
    $empty ? theme.dashboard.cautionTint : theme.colors.defaultHover};
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.metalGray};

  svg {
    flex-shrink: 0;
    width: ${({ theme }) => theme.fontSize.px10}rem;
    height: ${({ theme }) => theme.fontSize.px10}rem;
    margin-top: 0.15rem;
    color: ${({ theme, $empty }) => ($empty ? theme.dashboard.caution : theme.colors.iconGray)};
  }
`

const SettingsLink = styled(Link)`
  color: ${({ theme }) => theme.dashboard.infoDeep};
`
